import { AfterViewInit, Component, inject, Input, OnDestroy } from '@angular/core';
import * as echarts from 'echarts';
import { applyRoleVisibilityToOption } from '../../utils/chart-role-visibility.utils';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { ChartWrapperService, ChartWrapperInstance } from '../../shared-services/chart-wrapper.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import { buildConfigurableTooltipFormatter } from '../../utils/chart.utils';

import { CapitalCasePipe } from '../../pipes/capital-case.pipe';

@Component({
  selector: 'app-pie-chart',
  standalone: true,
  imports: [CapitalCasePipe],
  templateUrl: './pie-chart.component.html',
  styleUrls: ['./pie-chart.component.scss'],
})
export class PieChartComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;
  protected myChart!: echarts.ECharts;
  protected allSliceNames: string[] = [];

  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();
  private configService = inject(ConfigService);
  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private themeService = inject(ThemeService);
  private reflowSub?: Subscription;
  private themeSub?: Subscription;
  private lastChartData?: [{ name: string; value: number }];

  ngAfterViewInit(): void {
    // Attach wrapper as 'legend-only' chart type (pie/donut)
    const initialFilters = this.wrapper.attach(this.data, {
      onReload: (filters) => this.loadAndRenderChart(filters),
      getChart: () => this.myChart,
      getSeriesNames: () => this.allSliceNames,
      getCategories: () => [],
    }, 'legend-only');

    // Initial load (uses pre-existing filters if available, e.g. from auto-selection)
    this.loadAndRenderChart(initialFilters ?? undefined);

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.myChart?.resize();
    });

    // Re-render with fresh theme tokens when the user switches theme at runtime.
    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (this.lastChartData && this.myChart) this.renderChart(this.lastChartData);
    });
  }

  protected loadAndRenderChart(filters?: any): void {
    const chartDom = document.getElementById(this.data.widgetId);
    if (!chartDom) return;

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom, 'dvm');
    }

    this.myChart.showLoading();

    this.configService.getConfig(this.data.apiUrl, this.data, filters)?.subscribe({
      next: (res: any) => {
        if (!res) return;
        const dataKey = this.data?.dataKey;
        const chartData = dataKey ? res?.[dataKey] : (res?.data || res);
        if (Array.isArray(chartData)) {
          this.wrapper.setSourceData(chartData, this.data?.categoryKey || 'name');
        }
        this.renderChart(chartData);
        this.addResizeListener();
      },
      error: () => {
        this.myChart.showLoading();
      },
    });
  }

  protected renderChart(chartData?: [{ name: string; value: number }]): void {
    if (chartData) this.lastChartData = chartData;
    const theme = this.themeService.resolve(this.data?.themeOverrides);
    this.allSliceNames = (chartData || []).map((item) => item.name);

    const option = {
      title: {
        left: 'center',
      },
      tooltip: {
        trigger: 'item',
        backgroundColor: theme.tooltip?.bg || '#fff',
        borderColor: theme.tooltip?.borderColor || '#ccc',
        textStyle: {
          color: theme.tooltip?.textColor || '#333',
          fontSize: theme.tooltip?.fontSize || 13,
        },
        formatter: buildConfigurableTooltipFormatter(this.data?.chartConfig?.tooltip) || '{a} <br/>{b} : {c} ({d}%)',
      },
      legend: {
        orient: 'vertical',
        left: 'left',
        textStyle: {
          color: theme.chart?.legendTextColor || '#333',
        },
      },
      series: [
        {
          name: this.data.title || '',
          type: 'pie',
          radius: '50%',
          data: chartData,
          label: {
            color: theme.chart?.textColor || '#333',
          },
          labelLine: {
            lineStyle: {
              color: theme.chart?.axisLineColor || '#ccc',
            },
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: theme.chart?.emphasisShadowColor || 'rgba(0, 0, 0, 0.5)',
            },
          },
        },
      ],
    };

    applyRoleVisibilityToOption(option, this.data);

    this.myChart.setOption(option);
    setTimeout(() => {
      this.myChart.hideLoading();
    }, 1000);

    // Let wrapper re-apply visual state and wire click/legend events
    this.wrapper.afterRender();
  }

  protected addResizeListener(): void {
    window.addEventListener('resize', this.resizeHandler);
  }

  protected resizeHandler = () => this.myChart?.resize();

  ngOnDestroy(): void {
    this.wrapper.detach();
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    window.removeEventListener('resize', this.resizeHandler);
    this.myChart?.dispose();
  }
}
